import styled from '@emotion/styled';
import {
  Button,
  Divider,
  Drawer,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Paper,
  IconButton,
  Grid,
  Select,
} from '@material-ui/core';
// import { Button, Divider, Drawer,  TextField, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { ChangeEvent, FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import { Avatar as _Avatar } from '@material-ui/core';
import PagePreview from '../../entity/PagePreview';
import CreatePositionInput from '../../entity/CreatePositionInput';
import { User } from '@kaizenplatform/intelligence-entity';
import { NormalButton } from '../component/primitive/NormalButton';
import StyledFormLabel from '../component/primitive/StyledFormLabel';
import StyledInputField from '../component/primitive/StyledInputField';
import AddIcon from '@material-ui/icons/Add';
import NextIcon from '@material-ui/icons/NavigateNext';
import PrevIcon from '@material-ui/icons/NavigateBefore';
import {
  Header as DrawerHeader,
  Footer as DrawerFooter,
  Content as DrawerContent,
  Container as DrawerContainer,
} from '../component/compound/Drawer';
import useElementSize from '../../hooks/useElementSize';
import useImageUrlPreview from '../../hooks/useImageUrlPreview';
import _PreviewImage from '../../view/component/compound/PreviewImage';
import { useDebounce } from '../../hooks/useDebounce';
import ForumIcon from '@material-ui/icons/Forum';
import MoreMenu from '../component/compound/MoreMenu';
import MenuItem from '@material-ui/core/MenuItem';
import Spinner from '../component/primitive/Spinner';
import UploadButton from '../component/primitive/UploadButton';
import { useValidateUploadImage } from '../../hooks/useValidateUploadImage';
import bytesToSize from '../../utility/bytesToSize';

type Props = {
  currentPagePreview: PagePreview | null;
  updateComment: (id: string, body: string, shareId: string) => Promise<void>;
  isUpdatingComment: boolean;
  isCreatingReturnComment: boolean;
  isCreatingNewPostion: boolean;
  isAppendingPreviewPage: boolean;
  isGetCurrentPreviewPage: boolean;
  organizationId: string | undefined;
  appendPreviewPage: (
    args:
      | {
          pagePreviewId: string;
          content: string;
          contentOption: 'imageUrl';
          shareId: string;
        }
      | {
          pagePreviewId: string;
          content: File;
          contentOption: 'upload';
          shareId: string;
          organizationId: string;
        },
  ) => Promise<void>;
  currentUser: User | null;
  notifyUnimportant: (
    message: string,
    { messageValues, action, actionValues, isRaw, onClickAction, severity }?: any,
  ) => void;
  createNewPosition: (input: CreatePositionInput, body: string, shareId: string) => Promise<void>;
  createReturnComment: (positionId: string, body: string, shareId: string) => Promise<void>;
  changePathParam: (pageIndex: number) => void;
  onMount: () => void;
  pagePathParam?: number;
  onUnmount?: () => void;
  className?: string;
  pagePreviewsLoading: boolean;
};

type Point = {
  x: number;
  y: number;
};

type Position = {
  point: Point;
  order: number;
  previewComments: Array<PreviewComment>;
};

type PreviewComment = {
  body: string;
  createdAt: DateTime;
};

const POINT_RADIUS = 12;

const ZINDEX = {
  base: 1,
  addPageDrawer: 11,
};

//@see https://github.com/kaizenplatform/planbcd/blob/2fb0735123d42f012d2028bf76005711329fcb34/config/settings.yml#L27-L32
//2MB
const MAX_UPLOAD_SIZE = 2097152;

const PreviewCommentEditPage: FC<Props> = ({
  currentPagePreview,
  isGetCurrentPreviewPage,
  isCreatingReturnComment,
  isCreatingNewPostion,
  isAppendingPreviewPage,
  currentUser,
  updateComment,
  isUpdatingComment,
  createNewPosition,
  notifyUnimportant,
  createReturnComment,
  appendPreviewPage,
  pagePathParam,
  changePathParam,
  onMount,
  onUnmount,
  className,
  organizationId,
  // pagePreviewsLoading,
}) => {
  const [positions, setPositions] = useState<Array<Position>>();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPoint, setPoint] = useState<Point>();
  const [newComment, setNewComment] = useState<string>();
  const [returningComment, setReturningComment] = useState<string>();
  const [returningPositionId, setReturningPositionId] = useState<number>();
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>();
  // const [squareRef, { width }] = useElementSize();
  const [mainContainerRef, { width: mainContainerWidth }] = useElementSize();
  const [reducedRate, setReducedRate] = useState<number>(1);
  const [openAddPageDrawer, setOpenAddPageDrawer] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [contentOption, setContentOption] = useState<'imageUrl' | 'upload' | 'none'>('imageUrl');
  const {
    generatePreviewUrl,
    previewUrl,
    error: generatePreviewUrlError,
    generating,
    isValid: isValidImageUrl,
    reset: resetImageUrlPreview,
  } = useImageUrlPreview();
  const debounceImageUrl = useDebounce(imageUrl, 500);

  useEffect(() => {
    if (debounceImageUrl !== '') {
      generatePreviewUrl(imageUrl);
    } else {
      resetImageUrlPreview();
    }
  }, [debounceImageUrl]);
  const [imgRef, imgSize] = useElementSize();
  // const [defaultReducedRate, setDefaultReducedRate] = useState<number>(1);
  const [scaling, setScaling] = useState<number>(1.001);
  const [editingCommentId, setEditingCommentId] = useState<number>();
  const [editComment, setEditComment] = useState<string | undefined>(undefined);
  const {
    error: uploadImageError,
    file: uploadFile,
    onChange: onChangeUploadFile,
    previewUrl: uploadImagePreview,
    reset: resetUploadImage,
    isValid: isValidUploadImage,
  } = useValidateUploadImage(MAX_UPLOAD_SIZE);
  const asideContainerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    onMount();

    return () => {
      if (onUnmount) onUnmount();
    };
  }, []);

  const currentPage =
    (currentPagePreview && currentPagePreview.pages && currentPagePreview.pages[currentPageIndex]) ?? null;
  //indexが0から始まるのでcurrentPagePreview.pages.length - 1している
  const totalLength = (currentPagePreview && currentPagePreview.pages && currentPagePreview.pages.length - 1) ?? null;
  const canNext = totalLength !== null ? totalLength > currentPageIndex : false;
  const canPrev = totalLength !== null ? currentPageIndex > 0 : false;

  const handlePrev = () => {
    if (canPrev) {
      //ページ移動してもコメントが移動さきのページ残り続けるのでresetしている
      setNewComment('');
      setReturningComment('');
      setPoint(undefined);
      setReturningPositionId(undefined);
      changePathParam(currentPageIndex);
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };
  const handleNext = () => {
    if (canNext) {
      //ページ移動してもコメントが移動さきのページ残り続けるのでresetしている
      setNewComment('');
      setReturningComment('');
      setReturningPositionId(undefined);
      setPoint(undefined);
      changePathParam(currentPageIndex + 2);
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  const handleContentOptions = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContentOption(event.target.value as 'imageUrl' | 'upload' | 'none');
    resetImageUrlPreview();
    resetUploadImage();
    setImageUrl('');
  };

  const handleImageUrlChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setImageUrl(event.target.value);
  };

  const onSubmitNewPage = async () => {
    if (currentPagePreview === null) return;
    try {
      if (contentOption === 'imageUrl') {
        await appendPreviewPage({
          pagePreviewId: currentPagePreview.id,
          content: imageUrl,
          contentOption: 'imageUrl',
          shareId: currentPagePreview.id,
        });
      } else if (contentOption === 'upload') {
        if (uploadFile === null) {
          throw new Error('ファイルが選択されていません。');
        }
        if (typeof organizationId !== 'string') {
          throw new Error('組織IDが取得できませんでした。');
        }
        await appendPreviewPage({
          pagePreviewId: currentPagePreview.id,
          content: uploadFile,
          contentOption: 'upload',
          shareId: currentPagePreview.id,
          organizationId: organizationId,
        });
      } else {
        throw new Error('予期せぬコンテントです。');
      }

      notifyUnimportant('ページの追加に成功しました。', { severity: 'success' });
      onCloseAddPageDrawer();
      resetImageUrlPreview();
      resetUploadImage();
      setImageUrl('');
      setCurrentPageIndex((currentPageIndex) => {
        const lastIndexOfPages = currentPagePreview.pages !== undefined ? currentPagePreview.pages.length : undefined;
        return lastIndexOfPages ?? currentPageIndex;
      });
    } catch (e) {
      notifyUnimportant('ページの追加に失敗しました。', { severity: 'error' });
    }
  };

  const onCloseAddPageDrawer = () => {
    setContentOption('imageUrl');
    setImageUrl('');
    setOpenAddPageDrawer(false);
  };

  const toggleAddPageDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setOpenAddPageDrawer(open);
  };
  useEffect(() => {
    if (!currentPage) return;
    const img = new Image();
    img.src = currentPage.imageUrl;

    img.onload = function () {
      setOriginalImage(img);
    };

    if (
      pagePathParam &&
      currentPagePreview &&
      currentPagePreview.pages &&
      currentPagePreview.pages.length >= pagePathParam
    ) {
      changePathParam(pagePathParam);
      setCurrentPageIndex(pagePathParam - 1);
    } else {
      changePathParam(1);
    }
  }, [currentPage]);

  useEffect(() => {
    if (originalImage) {
      setReducedRate(originalImage.naturalWidth / imgSize.width);
      // setDefaultReducedRate(originalImage.naturalWidth / imgSize.width);
    }
  }, [imgSize, originalImage]);

  useEffect(() => {
    setReducedRate(reducedRate * scaling);
  }, [scaling]);

  const onClickElement = (event: MouseEvent) => {
    onCancelReturningComment();
    setPoint({
      x: Math.round(event.nativeEvent.offsetX * reducedRate),
      y: Math.round(event.nativeEvent.offsetY * reducedRate),
    });
  };

  const onSubmitNewComment = async () => {
    newComment && currentPoint
      ? positions
        ? setPositions([
            {
              point: currentPoint,
              order: positions.length + 1,
              previewComments: [{ body: newComment, createdAt: DateTime.local() }],
            },
            ...positions,
          ])
        : setPositions([
            { point: currentPoint, order: 1, previewComments: [{ body: newComment, createdAt: DateTime.local() }] },
          ])
      : undefined;

    if (newComment && currentPoint && currentPage && currentPage.id && currentPagePreview) {
      await createNewPosition(
        CreatePositionInput.fromJSON({
          pageId: currentPage.id,
          x: currentPoint.x,
          y: currentPoint.y,
        }),
        newComment,
        currentPagePreview.id,
      );
    }

    setPoint(undefined);
    setNewComment(undefined);
  };

  const onSubmitReturningComment = async (positionId: string) => {
    if (!!returningComment && currentPage && currentPagePreview) {
      // ここで同期的にcreateReturnCommentを処理しないと、setReturningPositionIdとsetReturningCommentが先に実行されて、loadingがうまく機能しない
      await createReturnComment(positionId, returningComment, currentPagePreview.id);
    }

    if (!!returningComment && !!positions) {
      //TODO: positionのlengthが１以上になることはないか確かめる
      positions[0].previewComments.push({ body: returningComment, createdAt: DateTime.local() });
      setPositions(positions.slice());
    }

    setReturningComment(undefined);
    setReturningPositionId(undefined);
  };

  const onCancelNewComment = () => {
    setPoint(undefined);
    setNewComment(undefined);
  };

  const handleChangeNewComment = (event: ChangeEvent<HTMLInputElement>) => {
    setNewComment(event.target.value);
  };

  const scrollToPoint = (positionId: string) => {
    const target = document.getElementById(`position_${positionId}`);
    if (target === null) {
      return;
    }
    // const top = target.getBoundingClientRect(;
    if (asideContainerRef.current === null) {
      return;
    }
    const offset = target.offsetTop;
    const gap = 64;
    const y = offset - gap;

    if (asideContainerRef.current !== null) {
      asideContainerRef.current?.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }
  };

  const onClickReturningButton = (positionId: string) => {
    onCancelReturningComment();
    onCancelNewComment();
    setReturningPositionId(Number(positionId));
    setTimeout(() => {
      scrollToPoint(positionId);
    }, 0);
  };

  const onCancelReturningComment = () => {
    setReturningPositionId(undefined);
    setReturningComment(undefined);
  };

  const handleChangeReturningComment = (event: ChangeEvent<HTMLInputElement>) => {
    setReturningComment(event.target.value);
  };

  // const onChangeScaling = (_: any, newValue: number | number[]) => {
  //   setScaling(newValue as number);
  // };
  const onChangeScaling = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: any;
    }>,
  ) => {
    setScaling(event.target.value as number);
  };

  // FYI：position.numは作られた順に割り当てられている idが大きいと最近作られたという意味。
  // idを比較して、新しい順に並び替えている。
  const onClickEditButton = (commentId: string, defaultValue: string) => {
    setEditingCommentId(Number(commentId));
    //FIXME: コントロールフィールドはデフォルトバリュー属性を使えないので、text fieldを開いたタイミングでdefault valueをstateからセットしている
    setEditComment(defaultValue);
  };

  const handleChangeEditComment = (event: ChangeEvent<HTMLInputElement>) => {
    setEditComment(event.target.value);
  };

  const onSubmitUpdatedComment = async (commentId: string) => {
    if (!!editComment && currentPagePreview && currentPagePreview.pages) {
      await updateComment(commentId, editComment, currentPagePreview.id);
      setEditComment(undefined);
      setEditingCommentId(undefined);
    }
  };

  const onCancelUpdateComment = () => {
    setEditComment(undefined);
    setEditingCommentId(undefined);
  };

  // numを比較して、新しい順に並び替えている。
  const formattedPositions =
    currentPage && currentPage.positions
      ? currentPage.positions.slice().sort((a, b) => {
          return Number(a.num) < Number(b.num) ? 1 : -1;
        })
      : null;

  const nextPositionNum =
    currentPage && currentPage.positions
      ? currentPage.positions.map((p) => p.num).reduce((a, b) => Math.max(a, b), 0) + 1
      : null;

  const canSubmitImageUrl = !!imageUrl && isValidImageUrl && !generating;

  const canSubmitUploadImage = uploadFile !== null && isValidUploadImage;

  const isLoading =
    isCreatingReturnComment || isCreatingNewPostion || isAppendingPreviewPage || isGetCurrentPreviewPage;

  return (
    <Root className={className}>
      <Drawer
        variant="temporary"
        anchor="right"
        open={openAddPageDrawer}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            onCloseAddPageDrawer();
          }
        }}
        PaperProps={{ style: { zIndex: ZINDEX.addPageDrawer } }}
      >
        <DrawerContainer>
          <DrawerContent>
            <DrawerHeader>
              <Title>ページの追加</Title>
            </DrawerHeader>
            <div>
              <FormControl fullWidth>
                <StyledFormLabel required>ファイルの登録</StyledFormLabel>
                <RadioGroup
                  row
                  aria-label="contentOptions"
                  name="row-radio-buttons-group"
                  onChange={handleContentOptions}
                >
                  <FormControlLabel
                    value="imageUrl"
                    control={<Radio disableRipple />}
                    label="画像URL"
                    checked={contentOption === 'imageUrl'}
                  />
                  <FormControlLabel
                    value="upload"
                    control={<Radio disableRipple />}
                    label="アップロード"
                    checked={contentOption === 'upload'}
                  />
                </RadioGroup>
                {contentOption === 'imageUrl' && (
                  <StyledInputField
                    value={imageUrl}
                    onChange={handleImageUrlChange}
                    placeholder="https://example.com/example.jpg"
                    helperText={<span>{generatePreviewUrlError}</span>}
                  />
                )}
                {contentOption === 'upload' && (
                  <div>
                    <UploadButton onChange={onChangeUploadFile} name="image">
                      ファイルを選択してください
                    </UploadButton>
                    <span>最大{bytesToSize(MAX_UPLOAD_SIZE)}</span>
                    {uploadFile !== null && <UploadImageName>{uploadFile.name}</UploadImageName>}
                    {uploadImageError && <UploadImageErrorText>{uploadImageError}</UploadImageErrorText>}
                  </div>
                )}
              </FormControl>
              {(previewUrl || uploadImagePreview) && (
                <PreviewImage url={previewUrl || uploadImagePreview} loading={generating} />
              )}
            </div>
          </DrawerContent>
          <DrawerFooter>
            <NormalButton variant="outlined" onClick={onCloseAddPageDrawer}>
              キャンセル
            </NormalButton>
            <NormalButton
              variant="contained"
              onClick={onSubmitNewPage}
              loading={isAppendingPreviewPage}
              disabled={
                contentOption === 'imageUrl'
                  ? !canSubmitImageUrl
                  : contentOption === 'upload'
                  ? !canSubmitUploadImage
                  : true
              }
            >
              追加する
            </NormalButton>
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
      <MainContainer ref={mainContainerRef}>
        <Header>
          <Title>{currentPagePreview?.title}</Title>
        </Header>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'relative',
              maxWidth: `${100 * scaling}%`,
              width: `${originalImage ? originalImage.naturalWidth * scaling : undefined}px`,
              margin: '0 auto',
            }}
          >
            <img
              id="id-img"
              src={currentPage?.imageUrl}
              ref={imgRef}
              onClick={onClickElement}
              style={{
                border: 'solid',
                // MainFooter分+@、bottomにmarginを設けている
                marginBottom: '100px',
                width: '100%',
              }}
            />
            {currentPage &&
              currentPage.positions?.map((position) => {
                return (
                  <div
                    id={'point_' + position.id}
                    style={{
                      width: POINT_RADIUS * 2,
                      height: POINT_RADIUS * 2,
                      borderRadius: POINT_RADIUS,
                      backgroundColor: Number(position.id) === returningPositionId ? '#2DD28D' : '#F5B800',
                      position: 'absolute',
                      top: Math.round(position.y / reducedRate) - POINT_RADIUS,
                      left: Math.round(position.x / reducedRate) - POINT_RADIUS,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#FFFFFF',
                      cursor: 'pointer',
                    }}
                    key={position.id}
                    onClick={() => onClickReturningButton(position.id)}
                  >
                    {position.num}
                  </div>
                );
              })}
            {currentPoint ? (
              <div
                style={{
                  width: POINT_RADIUS * 2,
                  height: POINT_RADIUS * 2,
                  borderRadius: POINT_RADIUS,
                  backgroundColor: '#2DD28D',
                  position: 'absolute',
                  top: Math.round(currentPoint.y / reducedRate) - POINT_RADIUS,
                  left: Math.round(currentPoint.x / reducedRate) - POINT_RADIUS,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FFFFFF',
                }}
                key={`next_point_${nextPositionNum}`}
              >
                {nextPositionNum}
              </div>
            ) : null}
          </div>
        </div>
        <MainNav width={mainContainerWidth}>
          <Grid
            container
            style={{
              height: '100%',
              alignItems: 'center',
              padding: '0 24px',
            }}
          >
            <Grid item xs={4}></Grid>
            <Grid item xs={4}>
              <PagenationContainer>
                <IconButton onClick={() => handlePrev()} disabled={!canPrev}>
                  <PrevIcon />
                </IconButton>
                <PageNation>{`${currentPageIndex + 1}/${totalLength !== null ? totalLength + 1 : ''}`}</PageNation>
                <IconButton onClick={() => handleNext()} disabled={!canNext}>
                  <NextIcon />
                </IconButton>
              </PagenationContainer>
            </Grid>
            <Grid item xs={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {/* <ScalingSlider
                value={scaling}
                onChange={onChangeScaling}
                max={defaultReducedRate > 1.0 ? 1.0 : Math.ceil(defaultReducedRate * 10) / 10}
                min={0.5}
                step={0.05}
                style={{ color: COLOR_MAIN }}
              /> */}
              <FormControl>
                <Select value={scaling} onChange={onChangeScaling}>
                  <MenuItem value={1.001}>100%</MenuItem>
                  <MenuItem value={0.9}>90%</MenuItem>
                  <MenuItem value={0.8}>80%</MenuItem>
                  <MenuItem value={0.7}>70%</MenuItem>
                  <MenuItem value={0.6}>60%</MenuItem>
                  <MenuItem value={0.5}>50%</MenuItem>
                  {/* <MenuItem value={0.1}>10%</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MainNav>
      </MainContainer>
      <AsideContainer ref={asideContainerRef}>
        <ActionContainer style={{ height: '64px' }}>
          <NormalButton startIcon={<AddIcon />} onClick={toggleAddPageDrawer(true)} variant="outlined">
            ページを追加
          </NormalButton>
        </ActionContainer>
        <Divider />
        {currentPoint ? (
          <CommentContainer>
            <CommentHeader>
              <div
                style={{
                  width: POINT_RADIUS * 2,
                  height: POINT_RADIUS * 2,
                  borderRadius: POINT_RADIUS,
                  backgroundColor: '#2DD28D',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#FFFFFF',
                }}
              >
                {nextPositionNum}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 8,
                }}
              >
                <Avatar src={currentUser?.profileImageUrl} style={{ width: 36, height: 36 }} />
                <div>
                  <div
                    style={{
                      padding: 2,
                      color: '#707889',
                    }}
                  >
                    {currentUser?.name}
                  </div>
                  <div
                    style={{
                      padding: 2,
                      color: '#707889',
                    }}
                  >
                    {DateTime.local().toFormat('yyyy/MM/dd HH:mm')}
                  </div>
                </div>
              </div>
            </CommentHeader>
            {isCreatingNewPostion ? (
              <Spinner />
            ) : (
              <div>
                <div>
                  <CommentTextField
                    multiline
                    variant="outlined"
                    rows={4}
                    value={newComment}
                    onChange={handleChangeNewComment}
                    // style={{ width: '100%', background: '#FFFFFF' }}
                  />
                </div>
                <ButtonContainer>
                  <OutlinedButton variant="outlined" onClick={onCancelNewComment}>
                    キャンセル
                  </OutlinedButton>
                  <SubmitButton variant="outlined" onClick={onSubmitNewComment} disabled={!newComment}>
                    送信
                  </SubmitButton>
                </ButtonContainer>
              </div>
            )}
          </CommentContainer>
        ) : null}
        {formattedPositions && formattedPositions.length > 0 ? (
          formattedPositions.map((position) => {
            return (
              <div key={'position_' + position.id} id={'position_' + position.id}>
                <CommentContainer>
                  <div>
                    {position.comments
                      ? position.comments.map((previewComment, commentIndex) => {
                          return (
                            <div key={'comment_' + commentIndex} style={{ marginBottom: 16 }}>
                              <CommentHeader>
                                {commentIndex === 0 ? (
                                  <div
                                    style={{
                                      width: POINT_RADIUS * 2,
                                      height: POINT_RADIUS * 2,
                                      borderRadius: POINT_RADIUS,
                                      backgroundColor:
                                        Number(position.id) === returningPositionId ? '#2DD28D' : '#F5B800',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      color: '#FFFFFF',
                                    }}
                                  >
                                    {position.num}
                                  </div>
                                ) : null}
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginLeft: commentIndex === 0 ? 8 : 32,
                                    width: '100%',
                                  }}
                                >
                                  <Avatar src={previewComment.author.profileImageUrl} />
                                  <div>
                                    <div
                                      style={{
                                        padding: 2,
                                        color: '#707889',
                                      }}
                                    >
                                      {previewComment.author.name}
                                    </div>
                                    <div
                                      style={{
                                        padding: 2,
                                        color: '#707889',
                                      }}
                                    >
                                      {DateTime.fromISO(previewComment.createdAt).toFormat('yyyy/MM/dd HH:mm')}{' '}
                                      {DateTime.fromISO(previewComment.createdAt) <
                                      DateTime.fromISO(previewComment.updatedAt)
                                        ? `(編集済み)`
                                        : ''}
                                    </div>
                                  </div>
                                  {/* 編集機能以外メニューがないので今はボタンごとhiddenする。 */}
                                  <div
                                    style={{ marginLeft: 'auto' }}
                                    hidden={
                                      currentUser !== null && previewComment.author.id === currentUser.id ? false : true
                                    }
                                  >
                                    <MoreMenu>
                                      <MenuItem
                                        onClick={() => onClickEditButton(previewComment.id, previewComment.body)}
                                      >
                                        編集
                                      </MenuItem>
                                    </MoreMenu>
                                  </div>
                                </div>
                              </CommentHeader>
                              {editingCommentId !== undefined &&
                              Number(previewComment.id) === editingCommentId &&
                              currentUser !== null &&
                              previewComment.author.id === currentUser.id ? (
                                isUpdatingComment ? (
                                  <Spinner />
                                ) : (
                                  <div>
                                    <div>
                                      <CommentTextField
                                        multiline
                                        variant="outlined"
                                        rows={4}
                                        value={editComment}
                                        onChange={handleChangeEditComment}
                                      />
                                    </div>
                                    <ButtonContainer>
                                      <OutlinedButton variant="outlined" onClick={onCancelUpdateComment}>
                                        キャンセル
                                      </OutlinedButton>
                                      <SubmitButton
                                        variant="outlined"
                                        onClick={() => onSubmitUpdatedComment(previewComment.id)}
                                        disabled={!editComment}
                                      >
                                        送信
                                      </SubmitButton>
                                    </ButtonContainer>
                                  </div>
                                )
                              ) : (
                                <CommentBody>{previewComment.body}</CommentBody>
                              )}
                            </div>
                          );
                        })
                      : undefined}
                  </div>
                </CommentContainer>
                {returningPositionId !== undefined && Number(position.id) === returningPositionId ? (
                  <CommentContainer>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 32,
                        marginBottom: 8,
                      }}
                    >
                      <Avatar src={currentUser?.profileImageUrl} />
                      <div>
                        <div
                          style={{
                            padding: 2,
                            color: '#707889',
                          }}
                        >
                          {currentUser?.name}
                        </div>
                        <div
                          style={{
                            padding: 2,
                            color: '#707889',
                          }}
                        >
                          {DateTime.local().toFormat('yyyy/MM/dd HH:mm')}
                        </div>
                      </div>
                    </div>
                    {isCreatingReturnComment ? (
                      <Spinner />
                    ) : (
                      <div>
                        <div>
                          <TextField
                            multiline
                            variant="outlined"
                            rows={4}
                            value={returningComment}
                            onChange={handleChangeReturningComment}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <ButtonContainer>
                          <OutlinedButton variant="outlined" onClick={onCancelReturningComment}>
                            キャンセル
                          </OutlinedButton>
                          <SubmitButton
                            variant="outlined"
                            onClick={() => onSubmitReturningComment(position.id)}
                            disabled={!returningComment}
                          >
                            送信
                          </SubmitButton>
                        </ButtonContainer>
                      </div>
                    )}
                  </CommentContainer>
                ) : null}
                {returningPositionId !== Number(position.id) ? (
                  <ReturnButtonContainer>
                    <OutlinedButton variant="outlined" onClick={() => onClickReturningButton(position.id)}>
                      返信
                    </OutlinedButton>
                  </ReturnButtonContainer>
                ) : undefined}
                <Divider />
              </div>
            );
          })
        ) : !currentPoint && !isLoading ? (
          <EmptyStatementContainer>
            <EmptyIcon style={{ marginBottom: '20px' }} />
            <EmptyText style={{ marginBottom: '20px' }}>コメントがありません</EmptyText>
            <EmptySubText>画面の上で言及したい箇所を指定し、コメントを入力しましょう。</EmptySubText>
          </EmptyStatementContainer>
        ) : null}
      </AsideContainer>
    </Root>
  );
};

const Root = styled('div')(`
  display: flex;
  height: calc(100vh - 64px);
`);

const MainContainer = styled('div')(`
  flex-grow:1;
  position: relative;
  padding: 36px;
  margin-bottom: 64px;
  height: 100%;
  overflow: auto;
`);

// MainNavをMainContainerの下にposition:fixedで表示したかったが、
//position:fixedは親要素の横幅ではなく、viewportの横幅に合わせるため、refから親要素の横幅を取得している
const MainNav = styled(Paper)<{ width: number }>(
  (props) => `
  bottom:0;
  left: 0;
  position: fixed;
  width:${props.width}px ;
  height: 64px;
  display: flex;
  justify-content:center;
  align-items: center;
`,
);

const EmptyStatementContainer = styled('div')(`
  height: calc(100% - 65px);
  width:80%;
  margin:0 auto;
  display:flex;
  flex-direction: column;
  align-items:center;
  justify-content: center;
  color: ${COLOR_TEXT_MAIN};
`);

const EmptyText = styled(Typography)(`
font-size: 20px;
text-align:center;
`);
const EmptySubText = styled(Typography)(`
font-size: 16px;
text-align:center;
`);

const EmptyIcon = styled(ForumIcon)(`
 &.MuiSvgIcon-root{
   width: 100px;
   height: 100px;
   fill: rgba(0, 0, 0, 0.26);
 }
`);

const PageNation = styled(Typography)(`
  display: flex;
  align-items: center;
  justify-content: center;
`);

const AsideContainer = styled(Paper)(`
   width: 450px;
   min-width: 450px;
   height: 100%;
   overflow:auto;
`);

const Header = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 36px;
`);

const CommentContainer = styled('div')(`
  padding: 16px;
`);

const CommentHeader = styled('div')(`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`);

const CommentBody = styled('div')(`
  margin: 4px 0px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 16px;
`);

const CommentTextField = styled(TextField)(`
  &:focus {
    border: 1.5px solid #707889;
  }
  width: 100%;
  background: #FFFFFF;
`);

const ReturnButtonContainer = styled('div')(`
  padding: 8px 16px;
  display: flex;
  justify-content: flex-end;
`);

const ButtonContainer = styled('div')(`
  padding: 8px 0px;
  display: flex;
  justify-content: flex-end;
`);

const SubmitButton = styled(Button)(`
  &:hover {
    background: #707889;
    border: #707889;
  }
  &:disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
  margin-left: 8px;
  color: #FFFFFF;
  background: #394151;
  border: #394151;
`);

const OutlinedButton = styled(Button)(`
  margin-left: 8px;
  color: #4C566C;
  background: #FFFFFF;
  border: 1.5px solid #E6E7E8;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
`);

const ActionContainer = styled('div')(`
width:100%;
display: grid;
padding: 16px;
align-items: center;
justify-content: space-between;
`);

const PagenationContainer = styled('div')(`
display: flex;
align-items: center;
justify-content: center;
`);

const PreviewImage = styled(_PreviewImage)(`
padding: 10px;
height: 300px;
 `);

// const ScalingSlider = styled(Slider)(`
//   width: 200px;
// `);

const Avatar = styled(_Avatar)(`
  width: 36px;
  height: 36px;
`);
const UploadImageErrorText = styled(Typography)(`
  font-size: 12;
  margin: 12px 0px;
  color: ${COLOR_TEXT_MAIN};
`);
const UploadImageName = styled(Typography)(`
  font-size: 12;
  margin: 12px 0px;
  color: ${COLOR_TEXT_MAIN};
  text-overflow: ellipsis;
  overflow: hidden;
white-space: nowrap;
`);
export default PreviewCommentEditPage;
